

















































import { Component, Prop, Vue, Watch, Ref } from "vue-property-decorator";
import { Chart } from "highcharts-vue";
import LogDataClient from "Clients/log-data-client";
import { CdsDataQuery } from "@/types/cdsDataQuery";
import { LogDataFilter } from "@/types/logDataFilter";
import { FeatureData } from "@/types/cds/featureData";
import { LogData } from "@/types/logData";
import Store from "@/store";
import { getModule } from "vuex-module-decorators";
import LogVariablesModule from "@/store/clients/LogVariables.module";
import CdsModule from "@/store/clients/Cds.module";
import Highcharts from "highcharts";
import HighchartsNoData from "highcharts/modules/no-data-to-display";
import { apiGet } from "@/services/api";
HighchartsNoData(Highcharts);

const LogVariables = getModule(LogVariablesModule, Store);
const Cds = getModule(CdsModule, Store);

export interface LoggingHistoryGraphQueryData {
  logDataFilter: LogDataFilter | undefined;
  cdsDataQuery: CdsDataQuery | undefined;
}
@Component({
  components: {
    HighCharts: Chart,
  },
})
export default class LoggingHistoryGraph extends Vue {
  @Prop({ type: Object }) readonly queryData?: LoggingHistoryGraphQueryData;
  @Ref() readonly chart!: any;

  logData: LogData | null = null;
  featureData: FeatureData | null = null;
  loadingState: "idle" | "loading" = "idle";
  graphZoomed = false;
  selectedXAxisMin = null;
  selectedXAxisMax = null;
  differenceThreshold = 55;
  logVariableCount = 0;
  cdsFeatureCount = 0;

  get isLoading(): boolean {
    return this.loadingState === "loading";
  }

  get hasLogData(): boolean {
    return this.logData !== null || this.featureData !== null;
  }

  getLogVariableName(index: number): string | undefined {
    const logVariableId = this.logData?.filter.logVariableIds[index];
    if (logVariableId) {
      return LogVariables.currentVesselLogVariables.find(logVariable => logVariable.id === logVariableId)?.name;
    }
  }

  getLogVariableDisplayPrecision(index: number): number | undefined {
    const logVariableId = this.logData?.filter.logVariableIds[index];
    if (logVariableId) {
      return LogVariables.currentVesselLogVariables.find(logVariable => logVariable.id === logVariableId)?.displayPrecision;
    }
  }

  getFeatureName(index: number): string | undefined {
    const featureId = this.featureData?.filter.featureIds[index];
    if (featureId) {
      return Cds.featureById(featureId)?.name;
    }
  }

  @Watch("queryData", { immediate: true })
  async onLogDataFilterChange(queryData: LoggingHistoryGraphQueryData): Promise<void> {
    this.loadingState = "loading";
    this.graphZoomed = false;
    this.logData = null;
    this.featureData = null;
    this.logVariableCount = 0;
    this.cdsFeatureCount = 0;
    if (queryData.logDataFilter) {
      const filter = queryData.logDataFilter;
      this.logVariableCount = filter.logVariableIds.split(",").length;
      if (this.logVariableCount > 0) {
        this.logData = await LogDataClient.batchFindLogData(filter.logVariableIds, filter.fromDate, filter.toDate, filter.granularity, filter.filterMinMax, filter.filterOutliers);
      }
    }
    if (queryData.cdsDataQuery) {
      const query = queryData.cdsDataQuery;
      this.cdsFeatureCount = query.cdsFeatureIds.split(",").length;
      if (this.cdsFeatureCount > 0) {
        this.featureData = await apiGet<FeatureData>(
          `/Cds/Data?vesselId=${query.vesselId}&featureIds=${query.cdsFeatureIds}&granularity=${query.granularity}&fromDate=${query.fromDate}&toDate=${query.toDate}&filterOutliers=${query.filterOutliers}`
        );
      }
    }
    this.loadingState = "idle";
  }

  get chartOptions(): unknown {
    return {
      time: {
        useUTC: true,
      },
      credits: {
        enabled: false,
      },
      chart: {
        marginTop: 50,
        type: "line",
        zoomType: "x",
        resetZoomButton: {
          theme: {
            display: "none",
          },
        },
        events: {
          //Updates the average value in legend when zooming on the graph
          selection: function (this: any, event: any) {
            setTimeout(() => {
              if (event.resetSelection) {
                this.graphZoomed = false;
              } else {
                this.graphZoomed = true;
              }

              //Custom code to update averge value in label when zooming the graph
              this.selectedXAxisMin = event.target.xAxis[0].min;
              this.selectedXAxisMax = event.target.xAxis[0].max;

              const xAxisMin = Math.ceil(event.target.xAxis[0].min);
              const xAxisMax = Math.ceil(event.target.xAxis[0].max);
              const seriesList = event.target.series;
              const selectedSeriesList: any[] = seriesList.map((series: any) => {
                return series.data.filter((point: any) => point.x >= xAxisMin && point.x <= xAxisMax);
              });

              for (const i in selectedSeriesList) {
                const isCdsFeature = seriesList[i].userOptions.customMetaData.isCdsFeature;
                const index = isCdsFeature ? Number(i) - this.logVariableCount : i;
                const featureName = isCdsFeature ? this.getFeatureName(index) : this.getLogVariableName(i);
                const averageValue = this.averageValue(index, isCdsFeature, selectedSeriesList[i]);
                seriesList[i].update({ name: `${featureName} ${averageValue}` }, true);
              }
            });
          }.bind(this),
        },
        style: {
          fontFamily: "Arial",
        },
      },
      title: {
        text: "",
      },
      yAxis: this.yAxis,
      xAxis: {
        type: "datetime",
        showEmpty: false,
      },
      series: this.series,
      noData: {
        style: {
          color: "#331714",
        },
      },
      lang: {
        noData: "Found no data for selected time period",
      },
    };
  }

  get series(): any {
    let series = this.logData?.filter.logVariableIds.map((_, index) => ({
      name: this.getLogVariableName(index) + this.averageValue(index, false),
      lineWidth: 2,
      yAxis: index,
      data: this.dateValuePairs(index),
      customMetaData: {
        isCdsFeature: false,
      },
    }));
    series = series ?? [];
    const cdsFeatureSeries = this.featureData?.filter.featureIds.map((_, index) => ({
      name: this.getFeatureName(index) + this.averageValue(index, true),
      lineWidth: 2,
      yAxis: index + this.logVariableCount,
      data: this.featureDateValuePairs(index),
      customMetaData: {
        isCdsFeature: true,
      },
    }));
    series.push(...(cdsFeatureSeries ?? []));
    return series;
  }

  dateValuePairs(seriesIndex: number): any {
    return this.logData?.data
      ? Object.entries(this.logData.data).map(([timestamp, pairValues]) => [
          this.parseAsUtc(timestamp),
          pairValues[seriesIndex] !== null ? Number(pairValues[seriesIndex].toFixed(this.getLogVariableDisplayPrecision(seriesIndex))) : null,
        ])
      : null;
  }

  featureDateValuePairs(seriesIndex: number): any {
    return this.featureData?.data
      ? Object.entries(this.featureData.data).map(([timestamp, pairValues]) => [Date.parse(timestamp), pairValues[seriesIndex] !== null ? Number(pairValues[seriesIndex].toFixed(2)) : null])
      : null;
  }

  yAxisMinMaxValues(seriesIndex: number): { max: number; min: number; unit: string | undefined } | null {
    const yAxisValues = this.logData?.data
      ? Object.entries(this.logData.data).map(([timestamp, pairValues]) => (pairValues[seriesIndex] !== null ? Number(pairValues[seriesIndex].toFixed(2)) : 0))
      : null;
    if (yAxisValues === null) return yAxisValues;
    return {
      min: Math.min(...yAxisValues),
      max: Math.max(...yAxisValues),
      unit: this.logData?.units[seriesIndex].name,
    };
  }

  featureDataYAxisMinMaxValues(seriesIndex: number): { max: number; min: number; unit: string | undefined } | null {
    const yAxisValues = this.featureData?.data
      ? Object.entries(this.featureData.data).map(([_, pairValues]) => (pairValues[seriesIndex] !== null ? Number(pairValues[seriesIndex].toFixed(2)) : 0))
      : null;
    if (yAxisValues === null) return yAxisValues;
    return {
      min: Math.min(...yAxisValues),
      max: Math.max(...yAxisValues),
      unit: this.featureData?.units[seriesIndex].name,
    };
  }

  parseAsUtc(timestamp: string): number {
    if (!timestamp.endsWith("Z")) {
      timestamp += "Z";
    }
    return Date.parse(timestamp);
  }

  //Highcharts only provide built in colors for the x-axis, so here we manually set the colors of the y-axis to match.
  get yAxisColors(): string[] {
    return ["#7cb5ec", "#434348", "#90ed7d", "#f7a35c", "#8085e9"];
  }

  get yAxis(): any {
    let axes = this.logData?.filter.logVariableIds.map((_, index) => {
      return {
        title: {
          text: "",
          style: { color: this.yAxisColors[index] },
        },
        labels: {
          format: "{value} " + this.logData?.units[index].name,
          style: { color: this.yAxisColors[index] },
        },
        ...this.yAxisMinMaxValues(index),
      };
    });
    axes = axes ?? [];
    const featureDataAxes = this.featureData?.filter.featureIds.map((_, index) => {
      return {
        title: {
          text: "",
          style: { color: this.yAxisColors[index + this.logVariableCount] },
        },
        labels: {
          format: "{value} " + this.featureData?.units[index].name,
          style: { color: this.yAxisColors[index + this.logVariableCount] },
        },
        ...this.featureDataYAxisMinMaxValues(index),
      };
    });
    axes.push(...(featureDataAxes ?? []));
    return this.combineCloseAxises(axes);
  }

  combineCloseAxises(axes: any): [] {
    let axesWithSameUnit: any = [];
    let yAxisOffset = 0;
    const logDataUnits = this.logData?.units ?? [];
    const cdsFeatureUnits = this.featureData?.units ?? [];
    const allUnits = logDataUnits.concat(cdsFeatureUnits);
    allUnits.forEach((unit, index) => {
      const yAxesMinMaxValues: any = [];
      axesWithSameUnit = axes.filter((axis: any) => unit.name === axis.unit);
      if (axesWithSameUnit.length >= 2) {
        //  get all min max values from axes with same unit
        axesWithSameUnit.forEach((axis: any) => yAxesMinMaxValues.push(axis?.min, axis?.max));
        // find highest and lowest values
        const highestMaxAndLowestMinValues = { min: Math.min(...yAxesMinMaxValues), max: Math.max(...yAxesMinMaxValues) };
        //  set highest max and lowest min values for axes with the same unit
        axesWithSameUnit.forEach((axis: any) => {
          axis.min = highestMaxAndLowestMinValues.min;
          axis.max = highestMaxAndLowestMinValues.max;
          axis.offset = yAxisOffset;
        });
        if (index > 1) yAxisOffset += 40;
      }
    });
    //  combine axes
    axes = [...new Set([...axes, ...axesWithSameUnit])];
    return axes;
  }

  resetZoom(): void {
    this.chart.chart.zoomOut();
  }

  averageValue(index: number, isCdsFeature: boolean, data?: []): string {
    let counter = 0;
    let value = 0;

    //When zooming the graph, the method is provided with data for the zoomed area only
    if (data) {
      const dataArray = data;

      dataArray.forEach(function (dataEntry: any) {
        if (dataEntry.y !== null) {
          value += dataEntry.y;
          counter++;
        }
      });
    } else {
      const dataArray = isCdsFeature ? (this.featureData?.data ? Object.values(this.featureData.data) : []) : this.logData?.data ? Object.values(this.logData.data) : [];

      dataArray.forEach(function (dataEntry) {
        if (dataEntry[index] !== null) {
          value += dataEntry[index];
          counter++;
        }
      });
    }

    const unit = isCdsFeature ? this.featureData?.units[index].name : this.logData?.units[index].name;
    const displayPrecision = isCdsFeature ? 2 : this.getLogVariableDisplayPrecision(index);
    return counter > 0 ? ` (Avg: ${Number((value / counter).toFixed(displayPrecision))} ${unit})` : " (No data)";
  }

  updateTimePeriod(): void {
    this.$emit("updateTimePeriod", { fromDateTime: this.selectedXAxisMin, toDateTime: this.selectedXAxisMax });
  }
}
